/*
 *   Copyright (c) 2020
 *   All rights reserved.
 */

import { ofType } from 'redux-observable';
import dayjs from 'dayjs';
import { merge } from 'rxjs';
import { switchMap, map, catchError, mergeMap } from 'rxjs/operators';
import { of } from 'rxjs/observable/of';
import { fromPromise } from 'rxjs/observable/fromPromise';

import * as actions from '@src/store/actions';
import * as types from '@src/store/types';
import { ui, session, user } from '@src/services';

import { TOAST_TYPE_SUCCESS, TOAST_TYPE_ERROR } from '@src/const/common';

export const getBindCreditCardInfoCall = (action$, store) => action$.pipe(
	ofType(types.GET_BIND_CREDITCARD_INFO_CALL),
	switchMap(({ payload: { fromLogin } }) => fromPromise(user.getBindCreditCardInfoCall()).pipe(
		switchMap(res => [
			actions.getBindCreditCardInfoDone(res),
			actions.rebindCreditCardInfoDone(false),
		]),
	)),
);

export const lockActionCreditCardCall = (action$, store) => action$.pipe(
	ofType(types.LOCK_ACTION_CREDITCARD_CALL),
	switchMap(({ payload }) => []),
);

export const deleteBindCreditCardInfoCall = (action$, store) => action$.pipe(
	ofType(types.DELETE_BIND_CREDITCARD_INFO_CALL),
	switchMap(({ payload: { CARD6NO, CARD4NO, data, initCreditCardsData } }) => fromPromise(user.deleteBindCreditCardInfoCall({ CARD6NO, CARD4NO })).pipe(
		switchMap(res => [
			actions.rebindCreditCardInfoCall({
				data,
				initCreditCardsData,
			}),
		]),
		catchError(err => {
			ui.toastShow({
				message: 'errors:serverError',
				type: TOAST_TYPE_ERROR,
			});
			return [actions.lockActionCreditCardCall(false)];
		}),
	)),
);

export const rebindCreditCardInfoCall = (action$, store) => action$.pipe(
	ofType(types.REBIND_CREDITCARD_INFO_CALL),
	switchMap(({ payload: { data } }) => fromPromise(user.rebindCreditCardInfoCall({ data })).pipe(
		switchMap(res => {
			ui.toastShow({
				message: 'doen:credit card update success',
				type: TOAST_TYPE_SUCCESS,
			});
			return [
				actions.getBindCreditCardInfoDone([res]),
				actions.rebindCreditCardInfoDone(true),
				actions.lockActionCreditCardCall(false),
			];
		}),
		catchError(err => {
			ui.toastShow({
				message: 'errors:serverError',
				type: TOAST_TYPE_ERROR,
			});
			return [
				actions.lockActionCreditCardCall(false),
				actions.rebindCreditCardInfoDone(false),
			];
		}),
	)),
);

export const getInvoiceListCall = (action$, store) => action$.pipe(
	ofType(types.GET_INVOICE_LIST_CALL),
	switchMap(({ payload: { owned_school_id } }) => fromPromise(user.getInvoiceListCall({ owned_school_id })).pipe(
		switchMap(res => [actions.getInvoiceListDone(res)]),
	)),
);

export const modifyInvoiceIncNumberCall = (action$, store) => action$.pipe(
	ofType(types.MODIFY_INVOICE_INC_NUMBER_CALL),
	switchMap(({ payload: { CARD6NO, CARD4NO, data, initCreditCardsData } }) => fromPromise(
		user.modifyInvoiceIncNumberCall({ CARD6NO, CARD4NO, data }),
	).pipe(
		switchMap(res => {
			ui.toastShow({
				message: 'doen:inc number update success',
				type: TOAST_TYPE_SUCCESS,
			});
			return [
				actions.getBindCreditCardInfoDone([
					{ ...initCreditCardsData, ...res },
				]),
			];
		}),
		catchError(err => {
			ui.toastShow({
				message: 'errors:serverError',
				type: TOAST_TYPE_ERROR,
			});
			return [];
		}),
	)),
);

export const downloadInvoiceCall = (action$, store) => action$.pipe(
	ofType(types.DOWNLOAD_INVOICE_CALL),
	switchMap(({ payload }) => fromPromise(user.downloadInvoice(payload)).pipe(
		switchMap(res => {
			const openLink = window.open(res);

			// 不能開窗就轉網址
			if (!openLink) {
				window.location.href = res;
			}

			return [actions.downloadInvoiceDoen(res)];
		}),
		catchError(err => {
			ui.toastShow({
				message: 'errors:serverError',
				type: TOAST_TYPE_ERROR,
			});

			return [];
		}),
	)),
);

// 取得當前使用方案
export const getUserCurrentPlanCall = (action$, store) => action$.pipe(
	ofType(types.GET_USER_CURRENT_PLAN_CALL),
	switchMap(({ payload: {} }) => fromPromise(user.getUserCurrentPlanCall({})).pipe(
		switchMap(res => [
			actions.getUserCurrentPlanDoen(res),
			actions.changeUserFormalPlanDOen(false),
		]),
		catchError(err => {
			ui.toastShow({
				message: 'errors:serverError',
				type: TOAST_TYPE_ERROR,
			});
			return [];
		}),
	)),
);

// 轉正式方案
export const changeUserFormalPlanCall = (action$, store) => action$.pipe(
	ofType(types.CHANGE_USER_FORMAL_PLAN_CALL),
	switchMap(({ payload: { data } }) => fromPromise(user.changeUserFormalPlanCall({ data })).pipe(
		switchMap(res => {
			ui.toastShow({
				message: 'doen:The full version is activated',
				type: TOAST_TYPE_SUCCESS,
			});
			console.log('轉正式方案', res);
			return [actions.changeUserFormalPlanDOen(true)];
		}),
		catchError(err => {
			ui.toastShow({
				message: 'errors:serverError',
				type: TOAST_TYPE_ERROR,
			});
			return [];
		}),
	)),
);

// 刪除試用
export const deleteUserTrialPlanCall = (action$, store) => action$.pipe(
	ofType(types.DELETE_USER_TRIAL_PLAN_CALL),
	switchMap(({ payload: {} }) => fromPromise(user.deleteUserTrialPlanCall({})).pipe(
		switchMap(res => {
			ui.toastShow({
				message: 'doen:delete trial success',
				type: TOAST_TYPE_SUCCESS,
			});
			console.log('刪除試用', res);
			return [actions.deleteUserTrialPlanDoen(true)];
		}),
		catchError(err => {
			ui.toastShow({
				message: 'errors:serverError',
				type: TOAST_TYPE_ERROR,
			});
			return [];
		}),
	)),
);

// 更換方案
export const putUserPlanCall = (action$, store) => action$.pipe(
	ofType(types.PUT_USER_PLAN_CALL),
	switchMap(({ payload: { data } }) => fromPromise(user.putUserPlanCall({ data })).pipe(
		switchMap(res => {
			ui.toastShow({
				message: `已變更方案，將從 ${dayjs(res.next_charge_at).format(
					'YYYY-MM-DD',
				)} 開始以新的方案計費`,
				type: TOAST_TYPE_SUCCESS,
			});
			ui.toastShow({
				message:
              'doen:The plan has been successfully upgraded, you can immediately enjoy more functions',
				type: TOAST_TYPE_SUCCESS,
			});
			console.log('更換方案', res);
			return [
				actions.getUserCurrentPlanDoen(res),
				actions.putUserPlanDoen(true),
			];
		}),
		catchError(err => {
			ui.toastShow({
				message: 'errors:serverError',
				type: TOAST_TYPE_ERROR,
			});
			return [];
		}),
	)),
);

// 反饋意見
export const opinionUserPlanCall = (action$, store) => action$.pipe(
	ofType(types.OPINION_USER_PLAN_CALL),
	switchMap(({ payload: { data } }) => fromPromise(user.opinionUserPlanCall({ data })).pipe(
		switchMap(res => {
			ui.toastShow({
				message:
              'doen:Thank you for being with us, your suggestions have been received and will be carefully evaluated as soon as possible',
				type: TOAST_TYPE_SUCCESS,
			});
			console.log('反饋意見', res);
			return [actions.opinionUserPlanDoen(true)];
		}),
		catchError(err => {
			ui.toastShow({
				message: 'errors:serverError',
				type: TOAST_TYPE_ERROR,
			});
			return [];
		}),
	)),
);

// 取消方案續約
export const cancelUserPlanCall = (action$, store) => action$.pipe(
	ofType(types.CANCEL_USER_PLAN_CALL),
	switchMap(({ payload: { reason } }) => fromPromise(user.cancelUserPlanCall({ reason })).pipe(
		switchMap(res => {
			ui.toastShow({
				message: 'doen:cancel plan success',
				type: TOAST_TYPE_SUCCESS,
			});
			console.log('取消方案續約', res);
			return [actions.cancelUserPlanDoen(true)];
		}),
		catchError(err => []),
	)),
);

// 更換大頭照
export const putUserProfilePictureCall = (action$, store) => action$.pipe(
	ofType(types.PUT_USER_PROFILE_PICTURE_CALL),
	switchMap(({ payload: { formData } }) => fromPromise(user.putUserProfilePictureCall({ formData })).pipe(
		switchMap(res => {
			ui.toastShow({
				message: '大頭照更新成功',
				type: TOAST_TYPE_SUCCESS,
			});
			return [
				actions.checkLoginCall({ fromLogin: false }),
				actions.putUserProfilePictureDone(false),
			];
		}),
		catchError(err => [actions.putUserProfilePictureFail(false)]),
	)),
);

// 更新個資
export const putUserProfileCall = (action$, store) => action$.pipe(
	ofType(types.PUT_USER_PROFILE_CALL),
	switchMap(({ payload: { form } }) => fromPromise(user.putUserProfileCall({ form })).pipe(
		switchMap(res => {
			ui.toastShow({
				message: '已儲存',
				type: TOAST_TYPE_SUCCESS,
			});
			return [
				actions.checkLoginCall({ fromLogin: false }),
				actions.putUserProfileDone(),
			];
		}),
		catchError(err => [actions.putUserProfileFail()]),
	)),
);

// 更新手機
export const putProfileMobileCall = (action$, store) => action$.pipe(
	ofType(types.PUT_PROFILE_MOBILE_CALL),
	switchMap(({ payload: { profile, form } }) => fromPromise(user.putProfileMobileCall({ form })).pipe(
		switchMap(res => [
			actions.putProfileMobileStatusCall(true),
			actions.verificationProfileMobileDoen(true),
		]),
		catchError(err => []),
	)),
);

// 手機驗證
export const verificationProfileMobileCall = (action$, store) => action$.pipe(
	ofType(types.VERIFICATION_PROFILE_MOBILE_STATUS_CALL),
	switchMap(({ payload: { profile, form } }) => fromPromise(user.verificationProfileMobileCall({ form })).pipe(
		switchMap(res => {
			ui.toastShow({ message: '手機驗證成功', type: TOAST_TYPE_SUCCESS });
			const { dialing_code, mobile_number } = res;
			profile = {
				...profile,
				dialing_code,
				mobile_number,
			};
			return [
				actions.setUser({ profile }),
				actions.putProfileMobileStatusCall(false),
				actions.verificationProfileMobileDoen(false),
			];
		}),
		catchError(err => []),
	)),
);

// 更換密碼
export const putProfilePasswordCall = (action$, store) => action$.pipe(
	ofType(types.PUT_PROFILE_PASSWORD_CALL),
	switchMap(({ payload: { form } }) => fromPromise(user.putProfilePasswordCall({ form })).pipe(
		switchMap(res => {
			ui.toastShow({ message: '密碼更新成功', type: TOAST_TYPE_SUCCESS });
			return [actions.putProfilePasswordDoen(true)];
		}),
		catchError(err => {
			if (err.response.status === 400) {
				ui.toastShow({ message: '新密碼不能與舊密碼相同', type: TOAST_TYPE_ERROR });
				return [actions.putProfilePasswordFail()];
			}

			if (err.response.status === 403) {
				ui.toastShow({ message: '密碼錯誤', type: TOAST_TYPE_ERROR });
				return [actions.putProfilePasswordFail()];
			}

			ui.toastShow({ message: 'errors:serverError', type: TOAST_TYPE_ERROR });
			return [actions.putProfilePasswordFail()];
		}),
	)),
);

// 綁定信箱
export const bindUserEmailCall = (action$, store) => action$.pipe(
	ofType(types.BIND_USER_EMAIL_CALL),
	switchMap(({ payload: { form } }) => fromPromise(user.bindUserEmailCall({ form })).pipe(
		switchMap(res => {
			ui.toastShow({
				message: '已將驗證信發送至您的信箱，請前往驗證',
				type: TOAST_TYPE_SUCCESS,
			});
			ui.toastShow({
				message: '密碼已修改',
				type: TOAST_TYPE_SUCCESS,
			});
			return [
				actions.checkLoginCall({ fromLogin: false }),
				actions.bindUserEmailDone(),
			];
		}),
		catchError(err => {
			const { message } = err.response.data;

			if (message === 'Conflict') {
				ui.toastShow({ message: '電子信箱已存在', type: TOAST_TYPE_ERROR });
			} else {
				ui.toastShow({ message: 'errors:serverError', type: TOAST_TYPE_ERROR });
			}
			return [actions.bindUserEmailFail()];
		}),
	)),
);

// 驗證信箱
export const postVerificationUserEmailCall = (action$, store) => action$.pipe(
	ofType(types.GET_VERIFICATION_USER_EMAIL_CALL),
	switchMap(({ payload: { form } }) => fromPromise(user.postVerificationUserEmailCall({ form })).pipe(
		switchMap(res => {
			ui.toastShow({ message: '密碼更新成功', type: TOAST_TYPE_SUCCESS });
			return [actions.checkLoginCall({ fromLogin: false })];
		}),
		catchError(err => {
			ui.toastShow({ message: 'errors:serverError', type: TOAST_TYPE_ERROR });
			return [];
		}),
	)),
);

export const getUserProfileCall = (action$, store) => action$.pipe(
	ofType(types.GET_USER_PROFILE_CALL),
	switchMap(() => fromPromise(user.getProfile({})).pipe(
		switchMap(res => [
			actions.setUser({ profile: res }),
			actions.getUserProfileDone(),
		]),
		catchError(err => [actions.getUserProfileFail(err)]),
	)),
);

//
export const bindOtherUsernameCall = (action$, store) => action$.pipe(
	ofType(types.BIND_OTHER_USERNAME_CALL),
	switchMap(({ payload: { type, category } }) => fromPromise(user.bindOtherUsernameCall({ type, category })).pipe(
		switchMap(res => {
			const { url } = res;

			window.location.href = url;

			return [actions.bindOtherUsernameDone(res)];
		}),
		catchError(err => {
			const {
				response: { status },
			} = err;

			if (status === 403) {
				ui.toastShow({ message: 'errors:bindError', type: TOAST_TYPE_ERROR });
			}

			return [actions.bindOtherUsernameFail(err)];
		}),
	)),
);

export const unbindOtherUsernameCall = (action$, store) => action$.pipe(
	ofType(types.UNBIND_OTHER_USERNAME_CALL),
	switchMap(({ payload: { type } }) => fromPromise(user.unbindOtherUsernameCall({ type })).pipe(
		switchMap(res => {
			ui.toastShow({ message: '已解除綁定', type: TOAST_TYPE_SUCCESS });

			return [
				actions.unbindOtherUsernameDone({ type }),
				actions.getUserProfileCall(),
			];
		}),
		catchError(err => [actions.unbindOtherUsernameFail({ type })]),
	)),
);

export const getLearningCenterEpic = (action$, store) => (
	action$.pipe(
		ofType(types.GET_LEARNING_CENTER_CALL),
		switchMap(({ payload }) => fromPromise(user.getLearningCenter(payload)).pipe(
			switchMap(res => [actions.getLearningCenterDone(res)]),
			catchError(err => [actions.getLearningCenterFail(err)]),
		)),
	)
);

export const getLearningCenterAssignCourseEpic = (action$, store) => (
	action$.pipe(
		ofType(types.GET_LEARNING_CENTER_ASSIGN_COURSE_CALL),
		mergeMap(({ payload }) => fromPromise(user.getLearningCenterAssignCourse(payload)).pipe(
			switchMap(res => [actions.getLearningCenterAssignCourseDone({ completed: payload.completed, res })]),
			catchError(err => [actions.getLearningCenterAssignCourseFail(err)]),
		)),
	)

);
export const getLearningCenterLearningPathEpic = (action$, store) => (
	action$.pipe(
		ofType(types.GET_LEARNING_CENTER_LEARNING_PATH_CALL),
		mergeMap(({ payload }) => fromPromise(user.getLearningCenterLearningPath(payload)).pipe(
			switchMap(res => [actions.getLearningCenterLearningPathDone({ status: payload.status, res })]),
			catchError(err => [actions.getLearningCenterLearningPathFail(err)]),
		)),
	)
);

export const getUserJoinedCoursesStatisticsEpic = (action$, store) => (
	action$.pipe(
		ofType(types.GET_USER_JOINED_COURSES_STATISTICS_CALL),
		switchMap(({ payload }) => fromPromise(user.getUserJoinedCoursesStatistics(payload)).pipe(
			switchMap(res => [actions.getUserJoinedCoursesStatisticsDone(res)]),
			catchError(err => [actions.getUserJoinedCoursesStatisticsFall(err)]),
		)),
	)
);

export const getUserJoinedLearningPathStatisticsEpic = (action$, store) => (
	action$.pipe(
		ofType(types.GET_USER_JOINED_LEARNING_PATH_STATISTICS_CALL),
		switchMap(({ payload }) => fromPromise(user.getUserJoinedLearningPathStatistics(payload)).pipe(
			switchMap(res => [actions.getUserJoinedLearningPathStatisticsDone(res)]),
			catchError(err => [actions.getUserJoinedLearningPathStatisticsFall(err)]),
		)),
	)
);

export const getUserLearningStatisticsEpic = (action$, store) => (
	action$.pipe(
		ofType(types.GET_USER_LEARNING_PATH_STATISTICS_CALL),
		switchMap(({ payload }) => fromPromise(user.getUserLearningStatistics(payload)).pipe(
			switchMap(res => [actions.getUserLearningStatisticsDone(res)]),
			catchError(err => [actions.getUserLearningStatisticsFall(err)]),
		)),
	)
);

export const getLearningRecordsMineEpic = (action$, store) => (
	action$.pipe(
		ofType(types.GET_LEARNING_RECORDS_MINE_CALL),
		switchMap(
			({ payload }) => fromPromise(user.getLearningRecordsMine(payload)).pipe(
				switchMap(res => of(actions.getLearningRecordsMineDone({ res }))),
				catchError(error => of(actions.getLearningRecordsMineFail({ error }))),
			),
		),
	)
);

export const getLearningLevelEpic = (action$, store) => (
	action$.pipe(
		ofType(types.GET_LEARNING_LEVEL_CALL),
		switchMap(() => fromPromise(user.getLearningLevel()).pipe(
			switchMap(res => [actions.getLearningLevelDone(res)]),
			catchError(err => [actions.getLearningLevelFail(err)]),
		)),
	)
);

export const postUserLanguageEpic = (action$, store) => (
	action$.pipe(
		ofType(types.POST_USER_LANGUAGE_CALL),
		switchMap(({ payload }) => fromPromise(user.postUserLanguage(payload)).pipe(
			switchMap(() => [actions.postUserLanguageDone(payload)]),
			catchError(err => [actions.postUserLanguageFail(err)]),
		)),
	)
);
